import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

import {
  filtersFromSearch,
  searchFromFilters
} from "../helpers/tracking-query.ts"
import { EMPTY_FILTERS, type TrackingFilters } from "../types/tracking.types.ts"

const NARROWING_KEYS = [
  "status",
  "document_type",
  "party_type",
  "sent_by",
  "sent_after",
  "sent_before",
  "q"
] as const

/**
 * The screen's filters live in the URL, so a tile's drill link, the back button
 * and a shared link all land on the same table. A tile names an exact set; any
 * other narrowing lets go of it rather than intersect with it.
 */
export function useTrackingFilters() {
  const [search, setSearch] = useSearchParams()
  const filters = useMemo(() => filtersFromSearch(search), [search])

  const replaceFilters = useCallback(
    (next: TrackingFilters) =>
      setSearch(searchFromFilters(next), { replace: true }),
    [setSearch]
  )
  const setFilters = useCallback(
    (patch: Partial<TrackingFilters>) => {
      const narrows = NARROWING_KEYS.some((key) => key in patch)
      replaceFilters({
        ...filters,
        ...(narrows && !("tile" in patch) ? { tile: null } : {}),
        ...patch
      })
    },
    [filters, replaceFilters]
  )
  const clearFilters = useCallback(
    () => replaceFilters({ ...EMPTY_FILTERS, ordering: filters.ordering }),
    [filters.ordering, replaceFilters]
  )
  return { filters, setFilters, replaceFilters, clearFilters }
}
